import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { getSmartRetry, releaseSmartRetryCase } from './api';
import { AssignModal } from './AssignModal';
import {
  CarrierPolicyLink,
  CARRIER_POLICY_COLUMN_HEADER,
  formatDuration,
  GridCell,
  LifecycleChip,
  ProductChip,
} from './shared-cells';
import { durationFilterColumnProps, managerTabGridProps } from './shared-grid';
import { MANAGER_TAB_DEFAULT_PAGE_SIZE, SmartRetryItem } from './types';
import { useGridView } from './view-state';

// ─── Smart Retry tab ──────────────────────────────────────────────────────────

export function SmartRetryTab() {
  const queryClient = useQueryClient();
  // Filter and sort are remembered while the app is open (see useGridView).
  const view = useGridView('smart-retry');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // The cases the open modal was started with. They stay fixed while some of them are dropped from the selection.
  const [assignModalIds, setAssignModalIds] = useState<number[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [releasingId, setReleasingId] = useState<number | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: MANAGER_TAB_DEFAULT_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>(
    view.restored.sortModel ?? [],
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    view.restored.filterModel ?? { items: [] },
  );
  const activeSortItem = sortModel[0];
  const activeFilterItem = filterModel.items.find(
    item =>
      item.value !== undefined &&
      item.value !== null &&
      String(item.value).trim() !== '',
  );
  const activeFilterValue =
    activeFilterItem?.value === undefined
      ? undefined
      : String(activeFilterItem.value);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      'manager-tab-smart-retry',
      paginationModel.page,
      paginationModel.pageSize,
      activeSortItem?.field,
      activeSortItem?.sort,
      activeFilterItem?.field,
      activeFilterValue,
    ],
    queryFn: () =>
      getSmartRetry({
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        sortBy: activeSortItem?.field,
        sortOrder: activeSortItem?.sort ?? undefined,
        filterBy: activeFilterItem?.field,
        filterValue: activeFilterValue,
      }),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    view.saveSort(model);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
    view.saveFilter(model);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const { mutate: release, isLoading: isReleasing } = useMutation({
    mutationFn: (applicationId: number) => releaseSmartRetryCase(applicationId),
    onMutate: applicationId => {
      setReleasingId(applicationId);
      setReleaseError(null);
    },
    onSettled: () => setReleasingId(null),
    onSuccess: (_, applicationId) => {
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(applicationId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['manager-tab-smart-retry'] });
    },
    onError: () => setReleaseError('Unable to release Smart Retry case.'),
  });

  const columns = useMemo<GridColDef<SmartRetryItem>[]>(
    () => [
      {
        field: 'carrierPolicyNumber',
        headerName: CARRIER_POLICY_COLUMN_HEADER,
        width: 120,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <CarrierPolicyLink
            applicationId={row.applicationId}
            carrierPolicyNumber={row.carrierPolicyNumber}
            termPolicyId={row.termPolicyId}
          />
        ),
      },
      {
        field: 'product',
        headerName: 'PRODUCT',
        width: 110,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ value }) => <ProductChip value={value} />,
      },
      {
        field: 'lifecycle',
        headerName: 'LIFECYCLE',
        width: 130,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ value }) => <LifecycleChip value={value} />,
      },
      {
        field: 'holdHours',
        headerName: 'HELD FOR',
        width: 120,
        type: 'number',
        ...durationFilterColumnProps,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ value }) => (
          <GridCell>
            <Typography variant="body2" color="text.secondary">
              {formatDuration(value as number)}
            </Typography>
          </GridCell>
        ),
      },
      {
        field: 'missingEvidence',
        headerName: 'MISSING EVIDENCE',
        width: 190,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }: { row: SmartRetryItem }) => {
          if (!row.missingEvidence?.length) {
            return (
              <GridCell>
                <Typography variant="caption" color="text.disabled">
                  —
                </Typography>
              </GridCell>
            );
          }
          return (
            <Box
              sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexWrap: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {row.missingEvidence.map(ev => (
                <Typography
                  key={ev}
                  variant="caption"
                  sx={{
                    bgcolor: 'warning.light',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    fontWeight: 600,
                    fontSize: 11,
                    flexShrink: 0,
                  }}
                >
                  {ev}
                </Typography>
              ))}
            </Box>
          );
        },
      },
      {
        field: 'retryCount',
        headerName: 'RETRY ATTEMPTS',
        width: 150,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }: { row: SmartRetryItem }) => (
          <GridCell>
            <Typography variant="body2" color="text.secondary">
              {row.retryCount}
            </Typography>
          </GridCell>
        ),
      },
      {
        field: '_release',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <GridCell>
            <Button
              size="small"
              variant="outlined"
              color="success"
              disabled={isReleasing}
              onClick={() => release(row.applicationId)}
              sx={{ fontSize: 12, minWidth: 76 }}
            >
              {releasingId === row.applicationId ? (
                <CircularProgress size={14} />
              ) : (
                'Release'
              )}
            </Button>
          </GridCell>
        ),
      },
    ],
    [isReleasing, release, releasingId],
  );

  if (isLoading && !data) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        Failed to load smart retry queue: {(error as Error)?.message}
      </Alert>
    );
  }

  const cases = data?.cases ?? [];
  const totalCount = data?.totalCount ?? 0;

  const currentPageIds = new Set(cases.map(c => c.applicationId));
  const currentPageSelectedIds = cases
    .map(c => c.applicationId)
    .filter(id => selectedIds.has(id));

  const handleSelectionChange = (newIds: GridRowSelectionModel) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      for (const id of currentPageIds) {
        next.delete(id);
      }
      for (const id of newIds as number[]) {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600}>
        Smart Retry queue
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Cases held by the engine during automated retry · Release lets the
        engine retry immediately · Assign overrides to a specific UW ·{' '}
        {totalCount} total
      </Typography>

      {releaseError && (
        <Alert
          severity="error"
          onClose={() => setReleaseError(null)}
          sx={{ mb: 1.5 }}
        >
          {releaseError}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="caption" color="text.secondary">
          {selectedIds.size > 0
            ? `${selectedIds.size} case${selectedIds.size !== 1 ? 's' : ''} selected`
            : 'Select cases to assign · or use Release to return to auto-assignment'}
        </Typography>
        {selectedIds.size > 0 && (
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setAssignModalIds([...selectedIds]);
              setAssignOpen(true);
            }}
          >
            Assign to UW ({selectedIds.size})
          </Button>
        )}
      </Box>

      <DataGrid
        rows={cases}
        columns={columns}
        getRowId={r => r.applicationId}
        checkboxSelection
        disableRowSelectionOnClick
        {...managerTabGridProps}
        initialState={view.initialState}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        rowSelectionModel={currentPageSelectedIds}
        onRowSelectionModelChange={handleSelectionChange}
        autoHeight
        paginationMode="server"
        rowCount={totalCount}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[25, 50, 100]}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        loading={isLoading}
        sx={{ bgcolor: 'background.paper' }}
      />

      <AssignModal
        applicationIds={assignModalIds}
        open={assignOpen}
        invalidateKey="manager-tab-smart-retry"
        onAssigned={assignedIds =>
          setSelectedIds(prev => {
            const next = new Set(prev);
            for (const id of assignedIds) {
              next.delete(id);
            }
            return next;
          })
        }
        onClose={(clearSelection?: boolean) => {
          setAssignOpen(false);
          if (clearSelection) setSelectedIds(new Set());
        }}
      />
    </Box>
  );
}
